import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import { loadGoogleMaps } from "../utils/loadGoogleMaps";
import CustomButton from "./CustomButton";
import formatDate from "../utils/formatDate";

/* ─── animations ─────────────────────────────────────────────────────── */
const ANIM_STYLES = `
@keyframes uf-row-out {
  0%   { opacity:1; transform:scaleY(1); max-height:60px; }
  60%  { opacity:0; transform:scaleY(0.2); }
  100% { opacity:0; transform:scaleY(0); max-height:0; margin:0; padding:0; border:0; }
}
.uf-row-removing td { animation: uf-row-out 0.4s ease forwards; overflow:hidden; }

@keyframes uf-toast-in  { from{opacity:0;transform:translateY(16px) scale(.95)} to{opacity:1;transform:none} }
@keyframes uf-toast-out { from{opacity:1;transform:none} to{opacity:0;transform:translateY(16px) scale(.95)} }
.uf-toast     { animation: uf-toast-in  0.28s cubic-bezier(.34,1.56,.64,1) forwards; }
.uf-toast-out { animation: uf-toast-out 0.28s ease forwards; }

@keyframes uf-spin { to{transform:rotate(360deg)} }
.uf-spin {
  display:inline-block; width:13px; height:13px;
  border:2px solid rgba(255,255,255,.35);
  border-top-color:#fff; border-radius:50%;
  animation:uf-spin .6s linear infinite; vertical-align:middle;
}

@keyframes uf-shimmer {
  0%  {background-position:-500px 0}
  100%{background-position: 500px 0}
}
.uf-skeleton {
  height:13px; border-radius:4px;
  background:linear-gradient(90deg,#e9ecef 25%,#f4f4f4 50%,#e9ecef 75%);
  background-size:500px 100%;
  animation:uf-shimmer 1.3s infinite;
}

/* ── map modal ── */
@keyframes uf-backdrop-in { from{opacity:0} to{opacity:1} }
@keyframes uf-modal-in    { from{opacity:0;transform:scale(0.94) translateY(12px)} to{opacity:1;transform:none} }

.uf-backdrop {
  position:fixed; inset:0; z-index:10000;
  background:rgba(0,0,0,0.58);
  display:flex; align-items:center; justify-content:center; padding:16px;
  animation:uf-backdrop-in 0.2s ease forwards;
}
.uf-modal {
  background:#fff; border-radius:14px;
  box-shadow:0 24px 60px rgba(0,0,0,0.28);
  width:100%; max-width:700px; overflow:hidden;
  display:flex; flex-direction:column; max-height:90vh;
  animation:uf-modal-in 0.25s cubic-bezier(.34,1.4,.64,1) forwards;
}
.uf-modal-head {
  display:flex; align-items:center; justify-content:space-between;
  padding:13px 18px;
  background:linear-gradient(90deg,#1d4ed8,#3b82f6);
  color:#fff; flex-shrink:0;
}
.uf-modal-head h2 { margin:0; font-size:0.95rem; font-weight:700; display:flex; align-items:center; gap:8px; }
.uf-modal-head .uf-close-btn {
  background:rgba(255,255,255,0.18); border:none; border-radius:8px;
  color:#fff; font-size:1.1rem; font-weight:700;
  width:32px; height:32px; cursor:pointer;
  display:flex; align-items:center; justify-content:center; transition:background .15s;
}
.uf-modal-head .uf-close-btn:hover { background:rgba(255,255,255,0.32); }
.uf-map-div { width:100%; height:420px; flex-shrink:0; }
.uf-modal-foot {
  padding:11px 18px; background:#f8fafc;
  border-top:1px solid #e2e8f0;
  display:flex; flex-wrap:wrap; gap:14px;
  font-size:0.78rem; color:#475569; flex-shrink:0;
}
.uf-info-chip { display:flex; align-items:center; gap:5px; }
.uf-info-chip .lbl { font-weight:700; color:#1e293b; }

/* view-on-map button */
.btn-view-map {
  display:inline-flex; align-items:center; gap:4px;
  padding:3px 10px;
  background:#eff6ff; border:1.5px solid #bfdbfe;
  color:#1d4ed8; border-radius:6px;
  font-size:0.72rem; font-weight:600;
  cursor:pointer; transition:all .15s; white-space:nowrap;
  line-height:1.6;
}
.btn-view-map:hover { background:#dbeafe; border-color:#93c5fd; }
`;

if (!document.getElementById("uf-styles")) {
  const el = document.createElement("style");
  el.id = "uf-styles"; el.textContent = ANIM_STYLES;
  document.head.appendChild(el);
}

/* ─── Toast ──────────────────────────────────────────────────────────── */
let _tid = 0;
function useToast() {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((msg, type = "success") => {
    const id = ++_tid;
    setToasts(p => [...p, { id, msg, type, fading: false }]);
    setTimeout(() => {
      setToasts(p => p.map(t => t.id === id ? { ...t, fading: true } : t));
      setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 320);
    }, 3200);
  }, []);

  const Portal = (
    <div style={{
      position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
      zIndex: 9999, display: "flex", flexDirection: "column", gap: 8,
      alignItems: "center", pointerEvents: "none",
    }}>
      {toasts.map(t => (
        <div key={t.id}
          className={`uf-toast${t.fading ? " uf-toast-out" : ""}`}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "11px 22px", borderRadius: 10, fontSize: "0.84rem",
            fontWeight: 600, boxShadow: "0 6px 22px rgba(0,0,0,.16)",
            background: t.type === "success" ? "#2f9e44" : "#c92a2a",
            color: "#fff", minWidth: 230, pointerEvents: "all",
          }}
        >
          <span style={{ fontSize: "1rem" }}>{t.type === "success" ? "✅" : "❌"}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
  return { push, Portal };
}

/* ─── Map Modal ───────────────────────────────────────────────────────── */
function MapModal({ loc, onClose }) {
  const mapDivRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);

  const lat = parseFloat(loc.freeze_latitude);
  const lng = parseFloat(loc.freeze_longitude);
  const validCoords = !isNaN(lat) && !isNaN(lng);

  /* close on Escape */
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* init Google Map */
  useEffect(() => {
    if (!validCoords || !mapDivRef.current) return;

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    loadGoogleMaps(apiKey)
      .then(() => {
        if (!mapDivRef.current) return;

        const googleMap = new window.google.maps.Map(mapDivRef.current, {
          zoom: 15,
          center: { lat, lng },
          mapTypeId: "roadmap",
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });

        mapInstanceRef.current = googleMap;

        const pinSVG = `
          <svg xmlns="http://www.w3.org/2000/svg" width="44" height="56" viewBox="0 0 44 56">
            <ellipse cx="22" cy="54" rx="9" ry="3" fill="rgba(0,0,0,0.22)"/>
            <path d="M22 2 C11 2 2 11 2 22 C2 35 22 52 22 52 C22 52 42 35 42 22 C42 11 33 2 22 2 Z"
                  fill="#2563eb" stroke="#fff" stroke-width="2.5"/>
            <circle cx="22" cy="22" r="10" fill="white" opacity="0.95"/>
            <path d="M22 14 C17.6 14 14 17.6 14 22 C14 26.4 17.6 30 22 30 C26.4 30 30 26.4 30 22 C30 17.6 26.4 14 22 14 Z"
                  fill="none" stroke="#2563eb" stroke-width="2"/>
            <circle cx="22" cy="22" r="3" fill="#2563eb"/>
          </svg>
        `;

        const icon = {
          url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(pinSVG),
          scaledSize: new window.google.maps.Size(44, 56),
          anchor: new window.google.maps.Point(22, 56),
        };

        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: googleMap,
          title: loc.client_name,
          icon,
          zIndex: 2000,
          optimized: false,
        });

        markerRef.current = marker;

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding:12px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:190px;">
              <div style="font-weight:700;font-size:14px;color:#1d4ed8;margin-bottom:6px;">
                📍 ${loc.client_name}
              </div>
              <div style="font-size:12px;color:#475569;line-height:1.7;">
                <div><span style="font-weight:600;color:#1e293b;">Type:</span> ${loc.retailer_type
              ? loc.retailer_type.charAt(0).toUpperCase() + loc.retailer_type.slice(1)
              : "—"
            }</div>
                <div><span style="font-weight:600;color:#1e293b;">Area:</span> ${loc.area || "—"}</div>
                <div><span style="font-weight:600;color:#1e293b;">Visit Date:</span> ${loc.visit_date || "—"}</div>
                <div>
  <span style="font-weight:600;color:#1e293b;">Address:</span>
  <div style="
    margin-top:2px;
    color:#475569;
    font-size:11px;
    line-height:1.4;
    max-width:220px;
    word-break:break-word;
  ">
    ${loc.address || "—"}
  </div>
</div>
                <div style="margin-top:6px;padding-top:6px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;">
                  ${lat.toFixed(6)}, ${lng.toFixed(6)}
                </div>
              </div>
            </div>
          `,
          maxWidth: 260,
        });

        marker.addListener("click", () => infoWindow.open(googleMap, marker));
        infoWindow.open(googleMap, marker);

        setMapLoading(false);
      })
      .catch((err) => {
        console.error("Map load error:", err);
        setMapError("Failed to load Google Maps.");
        setMapLoading(false);
      });

    return () => {
      if (markerRef.current) { try { markerRef.current.setMap(null); } catch (_) { } }
      mapInstanceRef.current = null;
    };
  }, [lat, lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="uf-backdrop" onClick={onBackdrop}>
      <div className="uf-modal">

        {/* header */}
        <div className="uf-modal-head">
          <h2>🗺️ Frozen Location</h2>
          <button className="uf-close-btn" onClick={onClose} title="Close (Esc)">✕</button>
        </div>

        {/* map or states */}
        {!validCoords ? (
          <div style={{
            height: 240, display: "flex", alignItems: "center", justifyContent: "center",
            flexDirection: "column", gap: 10, color: "#94a3b8"
          }}>
            <span style={{ fontSize: "2rem" }}>📍</span>
            <p style={{ margin: 0, fontSize: "0.9rem" }}>No valid coordinates available for this entry.</p>
          </div>
        ) : mapError ? (
          <div style={{
            height: 240, display: "flex", alignItems: "center", justifyContent: "center",
            flexDirection: "column", gap: 10, color: "#ef4444"
          }}>
            <span style={{ fontSize: "2rem" }}>⚠️</span>
            <p style={{ margin: 0, fontSize: "0.9rem" }}>{mapError}</p>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            {mapLoading && (
              <div style={{
                position: "absolute", inset: 0, zIndex: 10,
                background: "#f8fafc", display: "flex", alignItems: "center",
                justifyContent: "center", flexDirection: "column", gap: 12,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  border: "3px solid #dbeafe", borderTopColor: "#2563eb",
                  animation: "uf-spin .7s linear infinite",
                }} />
                <span style={{ fontSize: "0.83rem", color: "#64748b" }}>Loading map…</span>
              </div>
            )}
            <div ref={mapDivRef} className="uf-map-div" />
          </div>
        )}

        {/* footer info strip */}
        <div className="uf-modal-foot">
          <div className="uf-info-chip">
            <span className="lbl">Client:</span>
            <span>{loc.client_name}</span>
          </div>
          <div className="uf-info-chip">
            <span className="lbl">Area:</span>
            <span>{loc.area || "—"}</span>
          </div>
          <div className="uf-info-chip">
            <span className="lbl">Route:</span>
            <span>{loc.route || "—"}</span>
          </div>
          {validCoords && (
            <div className="uf-info-chip" style={{ marginLeft: "auto", color: "#94a3b8", fontSize: "0.72rem" }}>
              {lat.toFixed(5)}, {lng.toFixed(5)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── constants ──────────────────────────────────────────────────────── */
const CLIENT_TYPES = [
  { value: "chemist", label: "Chemist" },
  { value: "doctor", label: "Doctor" },
  { value: "stockist", label: "Stockist" },
];

const BADGE_STYLES = {
  chemist: { background: "#fff3bf", color: "#856404" },
  doctor: { background: "#d3f9d8", color: "#1a7a39" },
  stockist: { background: "#dbe4ff", color: "#3b5bdb" },
};

function TypeBadge({ type }) {
  const key = (type || "").toLowerCase();
  return (
    <span style={{
      display: "inline-block", padding: "2px 9px", borderRadius: 20,
      fontSize: "0.7rem", fontWeight: 700,
      ...(BADGE_STYLES[key] || { background: "#f1f3f5", color: "#495057" }),
    }}>
      {key ? key.charAt(0).toUpperCase() + key.slice(1) : "—"}
    </span>
  );
}

function SkeletonRows({ n = 5, cols = 9 }) {
  const widths = [30, 30, 140, 80, 100, 120, 80, 80, 80];
  return Array.from({ length: n }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((__, j) => (
        <td key={j} className="border p-2">
          <div className="uf-skeleton" style={{ width: widths[j] || 80 }} />
        </td>
      ))}
    </tr>
  ));
}

const fmt = (v) => (v == null || v === "" ? "—" : v);

/* ─── Main component ─────────────────────────────────────────────────── */
const UnfreezeLocation = () => {
  const [states, setStates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedClientType, setSelectedClientType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [removingIds, setRemovingIds] = useState(new Set());
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [mapLoc, setMapLoc] = useState(null);
  const { push, Portal } = useToast();

  /* load states */
  useEffect(() => {
    axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
      .then(r => setStates(r.data)).catch(console.error);
  }, []);

  /* load employees on state change */
  useEffect(() => {
    if (!selectedState) { setEmployees([]); return; }
    setSelectedEmployee("");
    axios.get(`${BASE_URL}/api/employees?state_id=${selectedState}`, { headers: getAuthHeaders() })
      .then(r => setEmployees(r.data)).catch(console.error);
  }, [selectedState]);

  /* clear on filter change */
  useEffect(() => {
    setLocations([]); setHasSearched(false);
    setSelectedIds(new Set()); setSearchQuery("");
  }, [selectedState, selectedEmployee, selectedClientType]);

  /* ── fetch ── */
  const handleGo = useCallback(() => {
    if (!selectedEmployee || !selectedClientType) {
      alert("Please select Employee and Client Type.");
      return;
    }
    setLocations([]); setHasSearched(false);
    setSelectedIds(new Set()); setSearchQuery("");
    setIsLoading(true);

    axios.get(
      // ✅ BASE_URL replaces hardcoded URL
      `${BASE_URL}/api/visits/get_freezed_location?employee_id=${selectedEmployee}&retailer_type=${selectedClientType}`,
      { headers: getAuthHeaders() }
    )
      .then(r => {
        const raw = r.data?.data || [];
        const normalized = raw.map(item => ({
          id: item.freeze_location_id,
          retailer_id: item.retailer_id,
          retailer_type: item.retailer_type,
          visit_date: item.visit_date,
          freeze_latitude: item.freeze_latitude,
          freeze_longitude: item.freeze_longitude,
          created_at: item.created_at,
          client_name: item.retailer_details?.chemist_name
            || item.retailer_details?.doctor_name
            || item.retailer_details?.name
            || "—",
          chemist_code: item.retailer_details?.code ?? null,
          contact_person: item.retailer_details?.contact_person || "—",
          mobile: item.retailer_details?.mobile || "—",
          area: item.retailer_details?.area || "—",
          route: item.retailer_details?.route || "—",
          city: item.retailer_details?.city || "—",
          state_name: item.retailer_details?.state || "—",
          address: item.retailer_details?.address || "—",
        }));
        setLocations(normalized);
        setHasSearched(true);
      })
      .catch(() => { push("Failed to fetch frozen locations.", "error"); setHasSearched(true); })
      .finally(() => setIsLoading(false));
  }, [selectedEmployee, selectedClientType, push]);

  /* ── search filter ── */
  const filteredLocations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter(loc =>
      loc.client_name.toLowerCase().includes(q) ||
      loc.area.toLowerCase().includes(q) ||
      loc.route.toLowerCase().includes(q) ||
      loc.city.toLowerCase().includes(q) ||
      String(loc.chemist_code || "").includes(q) ||
      loc.contact_person.toLowerCase().includes(q) ||
      loc.mobile.includes(q)
    );
  }, [locations, searchQuery]);

  /* ── checkboxes ── */
  const allVisibleChecked =
    filteredLocations.length > 0 &&
    filteredLocations.every(l => selectedIds.has(l.id));

  const toggleAll = () => {
    if (allVisibleChecked) {
      setSelectedIds(prev => { const n = new Set(prev); filteredLocations.forEach(l => n.delete(l.id)); return n; });
    } else {
      setSelectedIds(prev => { const n = new Set(prev); filteredLocations.forEach(l => n.add(l.id)); return n; });
    }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  /* ── single unfreeze ── */
  const handleUnfreeze = useCallback(async (loc) => {
    setRemovingIds(p => new Set(p).add(loc.id));
    await new Promise(r => setTimeout(r, 430));
    try {
      await axios.delete(
        // ✅ BASE_URL replaces hardcoded URL
        `${BASE_URL}/api/visits/delete/freezed_location`,
        { data: { ids: [loc.id] }, headers: getAuthHeaders() }
      );
      setLocations(p => p.filter(l => l.id !== loc.id));
      setSelectedIds(p => { const n = new Set(p); n.delete(loc.id); return n; });
      setRemovingIds(p => { const n = new Set(p); n.delete(loc.id); return n; });
      push(`"${loc.client_name}" unfrozen successfully!`, "success");
    } catch {
      setRemovingIds(p => { const n = new Set(p); n.delete(loc.id); return n; });
      push("Failed to unfreeze. Please try again.", "error");
    }
  }, [push]);

  /* ── bulk unfreeze ── */
  const handleBulkUnfreeze = useCallback(async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Unfreeze ${ids.length} selected location${ids.length > 1 ? "s" : ""}?`)) return;

    setIsBulkDeleting(true);
    ids.forEach(id => setRemovingIds(p => new Set(p).add(id)));
    await new Promise(r => setTimeout(r, 450));

    try {
      await axios.delete(
        // ✅ BASE_URL replaces hardcoded URL
        `${BASE_URL}/api/visits/delete/freezed_location`,
        { data: { ids }, headers: getAuthHeaders() }
      );
      setLocations(p => p.filter(l => !ids.includes(l.id)));
      setSelectedIds(new Set());
      setRemovingIds(p => { const n = new Set(p); ids.forEach(id => n.delete(id)); return n; });
      push(`${ids.length} location${ids.length > 1 ? "s" : ""} unfrozen successfully!`, "success");
    } catch {
      setRemovingIds(p => { const n = new Set(p); ids.forEach(id => n.delete(id)); return n; });
      push("Bulk unfreeze failed. Please try again.", "error");
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedIds, push]);

  const colCount = 10;

  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">

        {/* Title */}
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Unfreeze </span>
          <span className="text-blue-500">Locations</span>
        </h1>

        {/* ── Filters ── */}
        <div className="flex justify-center items-center">
          <div className="w-full max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold whitespace-nowrap">State</label>
                <select value={selectedState} onChange={e => setSelectedState(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg text-sm">
                  <option value="">Select State</option>
                  {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold whitespace-nowrap">Employee</label>
                <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}
                  disabled={!selectedState}
                  className="w-full px-4 py-2 border rounded-lg text-sm disabled:opacity-50">
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} | {emp.employee} | {emp.designation} | {emp.headquarter_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold whitespace-nowrap">Client Type</label>
                <select value={selectedClientType} onChange={e => setSelectedClientType(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg text-sm">
                  <option value="">Select Type</option>
                  {CLIENT_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                </select>
              </div>
            </div>

            {/* ✅ CustomButton replaces plain <button> for Go */}
            <div className="flex justify-center mt-6">
              <CustomButton
                onClick={handleGo}
                variant="go"
                disabled={isLoading}
                label={isLoading ? "Loading..." : "Go"}
              />
            </div>
          </div>
        </div>

        {/* ── Table section ── */}
        <div className="mt-4">

          {/* Toolbar */}
          {hasSearched && !isLoading && locations.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5 bg-white shadow-sm" style={{ minWidth: 220 }}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ color: "#9ca3af", flexShrink: 0 }}>
                  <circle cx="8.5" cy="8.5" r="5.75" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M13 13l3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search name, area, route, code…"
                  className="text-sm outline-none bg-transparent w-full" style={{ minWidth: 160 }} />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} className="text-gray-400 hover:text-gray-600 text-xs font-bold">✕</button>
                )}
              </div>

              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">
                  {filteredLocations.length} of {locations.length} record{locations.length !== 1 ? "s" : ""}
                  {selectedIds.size > 0 && (
                    <span className="ml-2 text-blue-600 font-semibold">· {selectedIds.size} selected</span>
                  )}
                </span>

                {/* ✅ CustomButton replaces plain <button> for Bulk Unfreeze */}
                {selectedIds.size > 0 && (
                  <button
                    onClick={handleBulkUnfreeze}
                    disabled={isBulkDeleting}
                    className="bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-xs font-semibold px-4 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    {isBulkDeleting
                      ? <><span className="uf-spin" /> Unfreezing…</>
                      : <>🔓 Unfreeze Selected ({selectedIds.size})</>
                    }
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="border rounded shadow">
            <table className="text-sm w-full border-collapse unlock-dcr-leave-table ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="border p-2 text-center" style={{ width: 36 }}>
                    {locations.length > 0 && (
                      <input type="checkbox" checked={allVisibleChecked} onChange={toggleAll}
                        title="Select all visible" style={{ cursor: "pointer", width: 15, height: 15 }} />
                    )}
                  </th>
                  <th className="border p-2 whitespace-nowrap">S.No</th>
                  <th className="border p-2 whitespace-nowrap">Code</th>
                  <th className="border p-2 whitespace-nowrap">Client Name</th>
                  <th className="border p-2 whitespace-nowrap">Contact Person</th>
                  <th className="border p-2 whitespace-nowrap">Mobile</th>
                  <th className="border p-2 whitespace-nowrap">Area / Route</th>
                  <th className="border p-2 whitespace-nowrap">Freezing Date</th>
                  <th className="border p-2 whitespace-nowrap">Frozen Location</th>
                  <th className="border p-2 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <SkeletonRows n={5} cols={colCount} />
                ) : filteredLocations.length > 0 ? (
                  filteredLocations.map((loc, idx) => {
                    const removing = removingIds.has(loc.id);
                    const isChecked = selectedIds.has(loc.id);
                    const hasCoords = loc.freeze_latitude != null && loc.freeze_longitude != null;

                    return (
                      <tr key={loc.id}
                        className={`hover:bg-gray-100 ${removing ? "uf-row-removing" : ""} ${isChecked ? "bg-blue-50" : ""}`}>

                        <td className="border p-2 text-center">
                          <input type="checkbox" checked={isChecked} onChange={() => toggleOne(loc.id)}
                            style={{ cursor: "pointer", width: 15, height: 15 }} />
                        </td>
                        <td className="border p-2 text-center">{idx + 1}</td>
                        <td className="border p-2 text-center text-gray-600">{fmt(loc.chemist_code)}</td>
                        <td className="border p-2 font-semibold whitespace-nowrap">{loc.client_name}</td>
                        <td className="border p-2">{fmt(loc.contact_person)}</td>
                        <td className="border p-2 whitespace-nowrap">{fmt(loc.mobile)}</td>
                        <td className="border p-2 text-xs text-gray-600">
                          <div className="font-medium text-gray-800">{fmt(loc.area)}</div>
                          <div className="text-gray-500 mt-0.5">{fmt(loc.route)}</div>
                        </td>
                        <td className="border p-2 text-center whitespace-nowrap">{formatDate(loc.created_at)}</td>

                        {/* ── Frozen Location column ── */}
                        <td className="border p-2 text-center">
                          {hasCoords ? (
                            <button className="btn-view-map" onClick={() => setMapLoc(loc)}>
                              🗺️ View on Map
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">No coords</span>
                          )}
                        </td>

                        {/* ✅ CustomButton replaces plain <button> for per-row Unfreeze */}
                        <td className="border p-2 text-center">
                          <button
                            onClick={() => handleUnfreeze(loc)}
                            disabled={removing || isBulkDeleting}
                            className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1 rounded flex items-center gap-1 mx-auto transition-colors whitespace-nowrap"
                          >
                            {removing
                              ? <><span className="uf-spin" /> Removing…</>
                              : <>🔓 Unfreeze</>
                            }
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={colCount} className="border p-10 text-center text-gray-400 text-sm">
                      {hasSearched
                        ? searchQuery
                          ? `No results matching "${searchQuery}"`
                          : "✅ No frozen locations found for the selected filters."
                        : "Select filters above and click Go to view frozen locations."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Map Modal ── */}
      {mapLoc && <MapModal loc={mapLoc} onClose={() => setMapLoc(null)} />}

      {Portal}
    </div>
  );
};

export default UnfreezeLocation;